<template>
    <div>
        <div class="container-fluid">
            <CRow class="pr-2">
                <CCol col="12" xl="12" class="p-0">
                    <CCard>
                        <CCardBody>
                            <a-card class="edit-card" style="margin:0;" :body-style="{padding:0}">
                                <span slot="title">
                                    <h6 class="w-100 mb-0 text-muted">Rezervácie</h6>
                                    Kalendár obsadenosti
                                </span>
                                <span slot="extra">
                                    <a-button-group class="mr-3">
                                        <a-button @click="previousMonth" icon="left"></a-button>
                                        <a-button @click="today">Dnes</a-button>
                                        <a-button @click="nextMonth" icon="right"></a-button>
                                    </a-button-group>
                                    <strong class="mr-3">{{ currentMonthYear }}</strong>
                                </span>

                                <div class="calendar-container">
                                    <!-- Header s dňami -->
                                    <div class="calendar-header">
                                        <div class="cabin-column-header">
                                            <div class="header-label">Ubytovacia jednotka</div>
                                        </div>
                                        <div class="days-grid-header">
                                            <!-- Riadok s mesiacmi -->
                                            <div class="header-row month-row">
                                                <div
                                                    v-for="monthSpan in monthSpans"
                                                    :key="'month-' + monthSpan.startIndex"
                                                    class="month-cell"
                                                    :style="{ flex: monthSpan.colspan, minWidth: (monthSpan.colspan * 40) + 'px' }">
                                                    <div class="month-name">{{ monthSpan.month }}</div>
                                                </div>
                                            </div>
                                            <!-- Riadok s dňami v týždni -->
                                            <div class="header-row weekday-row">
                                                <div
                                                    v-for="day in daysInMonth"
                                                    :key="'weekday-' + day.date"
                                                    :class="['day-cell-header', { 'weekend': day.isWeekend, 'today': day.isToday, 'past': day.isPast, 'month-end': day.isLastDayOfMonth }]">
                                                    <div class="day-name">{{ day.dayName }}</div>
                                                </div>
                                            </div>
                                            <!-- Riadok s číslami dní -->
                                            <div class="header-row daynumber-row">
                                                <div
                                                    v-for="day in daysInMonth"
                                                    :key="'daynumber-' + day.date"
                                                    :class="['day-cell-header', { 'weekend': day.isWeekend, 'today': day.isToday, 'past': day.isPast, 'month-end': day.isLastDayOfMonth }]">
                                                    <div class="day-number">{{ day.dayNumber }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Riadky s chatami a rezerváciami -->
                                    <div class="calendar-body">
                                        <div v-for="cabin in cabins" :key="cabin.id" class="cabin-row">
                                            <div class="cabin-column">
                                                <strong>{{ cabin.title }}</strong>
                                                <small class="d-block text-muted">{{ cabin.capacity }} osôb</small>
                                            </div>
                                            <div class="days-grid">
                                                <div
                                                    v-for="day in daysInMonth"
                                                    :key="day.date"
                                                    :class="['day-cell', { 'weekend': day.isWeekend, 'month-end': day.isLastDayOfMonth }]"
                                                    @click="selectDate(cabin.id, day.date)">

                                                    <!-- Blokované -->
                                                    <div
                                                        v-if="isDateBlocked(cabin.id, day.date) && !getBookingsForDay(cabin.id, day.date).length"
                                                        class="blocked-marker">
                                                    </div>
                                                </div>

                                                <!-- Rezervácie ako absolútne bloky -->
                                                <div
                                                    v-for="booking in getBookingsStartingForCabin(cabin.id)"
                                                    :key="'bk-'+booking.id"
                                                    :class="['booking-block', `status-${booking.order_status}`]"
                                                    :style="getBookingPosition(booking)"
                                                    @click.stop="viewBooking(booking.id)"
                                                    :title="booking.guest_name + ' (' + booking.check_in + ' - ' + booking.check_out + ')'">
                                                    <span class="booking-label">{{ booking.guest_name || 'Rezervácia #' + booking.id }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="px-3 pb-3 pt-3 border-top">
                                    <div class="row align-items-center">
                                        <div class="col-auto">
                                            <strong class="mr-3">Legenda:</strong>
                                        </div>
                                        <div class="col-auto">
                                            <div class="legend-item d-inline-flex align-items-center mr-3">
                                                <span class="legend-color status-pending"></span>
                                                <span>Čaká na potvrdenie</span>
                                            </div>
                                            <div class="legend-item d-inline-flex align-items-center mr-3">
                                                <span class="legend-color status-payment_recieved"></span>
                                                <span>Platba prijatá</span>
                                            </div>
                                            <div class="legend-item d-inline-flex align-items-center mr-3">
                                                <span class="legend-color status-completed"></span>
                                                <span>Dokončená</span>
                                            </div>
                                            <div class="legend-item d-inline-flex align-items-center">
                                                <span class="legend-color blocked"></span>
                                                <span>Blokované</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </a-card>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </div>
    </div>
</template>

<script>
import {dataService} from "../_services";
import moment from 'moment';
import 'moment/locale/sk';

export default {
    name: "cabin-calendar",
    data() {
        return {
            cabins: [],
            bookings: [],
            blockedDates: [],
            currentMonth: moment(),
        };
    },
    computed: {
        currentMonthYear() {
            return this.currentMonth.format('MMMM YYYY');
        },
        daysInMonth() {
            const days = [];
            const today = moment();
            const startDate = this.currentMonth.clone().subtract(7, 'days');
            const endDate = this.currentMonth.clone().add(30, 'days');
            const todayStr = today.format('YYYY-MM-DD');

            for (let day = startDate.clone(); day.isSameOrBefore(endDate); day.add(1, 'day')) {
                const nextDay = day.clone().add(1, 'day');
                const isLastDayOfMonth = day.month() !== nextDay.month();

                days.push({
                    date: day.format('YYYY-MM-DD'),
                    dayName: day.format('dd'),
                    dayNumber: day.format('D'),
                    month: day.format('MMMM'),
                    monthYear: day.format('MMMM YYYY'),
                    isWeekend: day.day() === 0 || day.day() === 6,
                    isToday: day.format('YYYY-MM-DD') === todayStr,
                    isPast: day.isBefore(today, 'day'),
                    isLastDayOfMonth: isLastDayOfMonth
                });
            }

            return days;
        },
        monthSpans() {
            const spans = [];
            let currentMonth = null;
            let count = 0;
            let startIndex = 0;

            this.daysInMonth.forEach((day, index) => {
                if (currentMonth !== day.monthYear) {
                    if (currentMonth !== null) {
                        spans.push({
                            month: currentMonth,
                            colspan: count,
                            startIndex: startIndex
                        });
                    }
                    currentMonth = day.monthYear;
                    count = 1;
                    startIndex = index;
                } else {
                    count++;
                }
            });

            // Pridaj posledný mesiac
            if (currentMonth !== null) {
                spans.push({
                    month: currentMonth,
                    colspan: count,
                    startIndex: startIndex
                });
            }

            return spans;
        }
    },
    mounted() {
        this.fetchCabins();
    },
    methods: {
        fetchCabins() {
            dataService.axiosFetch('content/cabins').then(results => {
                // Konvertuj objekt na pole a ignoruj 'parent'
                const data = results.data || results;
                this.cabins = Object.values(data).filter(item => item && item.id);
                this.fetchCalendar();
            });
        },
        fetchCalendar() {
            const startDate = this.currentMonth.clone().subtract(7, 'days').format('YYYY-MM-DD');
            const endDate = this.currentMonth.clone().add(30, 'days').format('YYYY-MM-DD');

            const params = {
                start_date: startDate,
                end_date: endDate
            };

            dataService.axiosPost('cabin-calendar', params).then(result => {
                const data = (result && result.data && result.data.data) ? result.data.data : (result && result.data) ? result.data : result;

                if (data.cabins && Array.isArray(data.cabins)) {
                    // Nový formát - reservations per cabin
                    let allBookings = [];
                    let allBlocked = [];
                    data.cabins.forEach(c => {
                        if (c.reservations) {
                            c.reservations.forEach(r => {
                                allBookings.push({ ...r, cabin_id: String(c.cabin_id) });
                            });
                        }
                        if (c.blocked) {
                            c.blocked.forEach(b => {
                                allBlocked.push({ ...b, cabin_id: String(c.cabin_id) });
                            });
                        }
                    });
                    this.bookings = allBookings;
                    this.blockedDates = allBlocked;
                } else {
                    // Fallback starý formát
                    this.bookings = data.bookings || [];
                    this.blockedDates = data.blocked || [];
                }
            });
        },
        getBookingsForDay(cabinId, date) {
            return this.bookings.filter(b => {
                return String(b.cabin_id) === String(cabinId) &&
                       date >= b.check_in &&
                       date < b.check_out;
            });
        },
        getBookingsStartingForCabin(cabinId) {
            const startDate = this.daysInMonth[0] ? this.daysInMonth[0].date : '';
            const endDate = this.daysInMonth.length ? this.daysInMonth[this.daysInMonth.length - 1].date : '';
            return this.bookings.filter(b => {
                return String(b.cabin_id) === String(cabinId) &&
                       b.check_in < endDate &&
                       b.check_out > startDate;
            });
        },
        getBookingPosition(booking) {
            const days = this.daysInMonth;
            if (!days.length) return { display: 'none' };
            const firstDate = days[0].date;
            const cellWidth = 40; // min-width of day-cell

            // Začiatok - koľko buniek od začiatku
            let startIdx = days.findIndex(d => d.date === booking.check_in);
            if (startIdx === -1) startIdx = 0; // začal pred viditeľným rozsahom

            // Koniec
            let endIdx = days.findIndex(d => d.date === booking.check_out);
            if (endIdx === -1) endIdx = days.length; // končí za viditeľným rozsahom

            const span = endIdx - startIdx;
            if (span <= 0) return { display: 'none' };

            return {
                left: `calc(${startIdx} * (100% / ${days.length}) + 2px)`,
                width: `calc(${span} * (100% / ${days.length}) - 4px)`
            };
        },
        isBookingStart(booking, date) {
            return booking.check_in === date;
        },
        isDateBlocked(cabinId, date) {
            return this.blockedDates.some(b => {
                if (String(b.cabin_id) === String(cabinId)) {
                    if (b.date_from && b.date_to) {
                        return date >= b.date_from && date <= b.date_to;
                    }
                    if (b.date) return b.date === date;
                }
                return false;
            });
        },
        selectDate(cabinId, date) {
            // Funkcia pre výber dátumu (blokovanie)
            console.log('Selected:', cabinId, date);
        },
        viewBooking(bookingId) {
            this.$router.push(`/cabin-booking/${bookingId}`);
        },
        previousMonth() {
            this.currentMonth = this.currentMonth.clone().subtract(7, 'days');
            this.fetchCalendar();
        },
        nextMonth() {
            this.currentMonth = this.currentMonth.clone().add(7, 'days');
            this.fetchCalendar();
        },
        today() {
            this.currentMonth = moment();
            this.fetchCalendar();
        }
    }
};
</script>

<style scoped>
.calendar-container {
    overflow-x: auto;
}

.calendar-header {
    display: flex;
    border-bottom: 2px solid #e8e8e8;
    background: #fafafa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.cabin-column-header {
    width: 200px;
    min-width: 200px;
    border-right: 1px solid #e8e8e8;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cabin-column-header .header-label {
    padding: 12px;
    font-weight: 500;
    text-align: center;
}

.days-grid-header {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.header-row {
    display: flex;
    border-bottom: 1px solid #e8e8e8;
}

.header-row:last-child {
    border-bottom: none;
}

.day-cell-header {
    flex: 1;
    min-width: 40px;
    padding: 4px;
    text-align: center;
    border-right: 1px solid #f0f0f0;
}

.day-cell-header.weekend {
    background: #f5f5f5;
}

.day-cell-header.today {
    background: #e6f7ff;
}

.day-cell-header.past {
    opacity: 0.5;
}

.day-cell-header.month-end {
    border-right: 2px solid #d9d9d9;
}

.month-row {
    min-height: 24px;
}

.month-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 2px solid #d9d9d9;
    background: #fafafa;
}

.month-cell:last-child {
    border-right: 1px solid #f0f0f0;
}

.weekday-row {
    min-height: 20px;
}

.daynumber-row {
    min-height: 28px;
}

.month-name {
    font-size: 12px;
    font-weight: 600;
    color: #262626;
    text-transform: uppercase;
}

.day-name {
    font-size: 11px;
    color: #8c8c8c;
    text-transform: uppercase;
}

.day-number {
    font-size: 14px;
    font-weight: 600;
}

.cabin-column {
    width: 200px;
    min-width: 200px;
    padding: 12px;
    border-right: 1px solid #e8e8e8;
    background: #fafafa;
    font-weight: 500;
}

.days-grid {
    display: flex;
    flex: 1;
    position: relative;
}

.day-header {
    flex: 1;
    min-width: 40px;
    padding: 8px 4px;
    text-align: center;
    border-right: 1px solid #f0f0f0;
}

.day-header.weekend {
    background: #f5f5f5;
}

.day-header.today {
    background: #e6f7ff;
    border: 2px solid #1890ff;
}

.day-header.past {
    opacity: 0.5;
}

.day-name {
    font-size: 11px;
    color: #8c8c8c;
    text-transform: uppercase;
}

.day-number {
    font-size: 14px;
    font-weight: 600;
    margin-top: 2px;
}

.calendar-body {
    background: #fff;
}

.cabin-row {
    display: flex;
    border-bottom: 1px solid #e8e8e8;
    min-height: 60px;
}

.cabin-row:hover {
    background: #fafafa;
}

.day-cell {
    flex: 1;
    min-width: 40px;
    border-right: 1px solid #f0f0f0;
    position: relative;
    padding: 4px 2px;
    cursor: pointer;
}

.day-cell.weekend {
    background: #fafafa;
}

.day-cell:hover {
    background: #f0f0f0;
}

.day-cell.month-end {
    border-right: 2px solid #d9d9d9;
}

.booking-block {
    position: absolute;
    top: 8px;
    height: calc(100% - 16px);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 11px;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    z-index: 5;
    display: flex;
    align-items: center;
}

.booking-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.blocked-marker {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 6px);
}

.booking-block:hover {
    opacity: 0.9;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.booking-block.status-pending {
    background: #fa8c16;
    border: 1px solid #d46b08;
}

.booking-block.status-payment_recieved {
    background: #1890ff;
    border: 1px solid #096dd9;
}

.booking-block.status-completed {
    background: #52c41a;
    border: 1px solid #389e0d;
}

.booking-block.status-storno {
    background: #f5222d;
    border: 1px solid #cf1322;
    opacity: 0.5;
}

.booking-block.blocked {
    background: #8c8c8c;
    border: 1px solid #595959;
}

.legend-item {
    display: flex;
    align-items: center;
}

.legend-color {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 8px;
    border-radius: 2px;
}

.legend-color.status-pending {
    background: #fa8c16;
}

.legend-color.status-payment_recieved {
    background: #1890ff;
}

.legend-color.status-completed {
    background: #52c41a;
}

.legend-color.blocked {
    background: #8c8c8c;
}
</style>
